import React, { useState, useMemo } from 'react';
import { IconFlashFilled, IconUserAvatar, IconThumbsUp, IconThumbsDown, IconCopy, IconExternalLink } from 'src/presentations/components/atom/icons';
import IconEdit from 'src/assets/images/icons/edit-2.svg';
import IconTrash from 'src/assets/images/icons/trash.svg';
import { Message, ContentBlock, ActiveToolCall } from '../../pages/askFreya/types';
import StatCard from '../../molecules/askFreya/StatCard';
import { RichTable } from '../../molecules/askFreya/RichTable';
import { ActivityFeed } from '../../molecules/askFreya/ActivityFeed';

interface MessageItemProps {
  message: Message;
}

/** Render inline markdown: **bold**, *italic*, `code`, [links](url) */
const InlineMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const parts = useMemo(() => {
    const tokens: React.ReactNode[] = [];
    // Regex: **bold**, *italic*, `code`, [text](url)
    // Italic requires non-space after opening * and non-space before closing *,
    // and the * must not be adjacent to another * (which would be bold syntax).
    const regex = /(\*\*(.+?)\*\*|(?<!\*)\*(?!\*|\s)(.+?)(?<!\s|\*)\*(?!\*)|`(.+?)`|\[(.+?)\]\((.+?)\))/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        tokens.push(text.slice(lastIndex, match.index));
      }
      if (match[2]) {
        tokens.push(
          <strong key={match.index} className='font-semibold'>
            {match[2]}
          </strong>
        );
      } else if (match[3]) {
        tokens.push(<em key={match.index}>{match[3]}</em>);
      } else if (match[4]) {
        tokens.push(
          <code key={match.index} className='px-4 py-1 bg-neutral-10 rounded text-13 font-mono'>
            {match[4]}
          </code>
        );
      } else if (match[5] && match[6]) {
        tokens.push(
          <a key={match.index} href={match[6]} className='text-primary underline hover:text-primary/80' target='_blank' rel='noopener noreferrer'>
            {match[5]}
          </a>
        );
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      tokens.push(text.slice(lastIndex));
    }
    return tokens;
  }, [text]);

  return <>{parts}</>;
};

/** Collapsible group of tool calls — shows max 3, expandable */
const ToolCallGroup: React.FC<{ calls: { key: string; toolCall: ActiveToolCall }[] }> = ({ calls }) => {
  const [expanded, setExpanded] = useState(false);
  const MAX_VISIBLE = 1;
  const visible = expanded ? calls : calls.slice(0, MAX_VISIBLE);
  const hiddenCount = calls.length - MAX_VISIBLE;

  return (
    <div className='mb-6'>
      {visible.map(({ key, toolCall: tc }) => (
        <div key={key} className='flex items-center gap-6 text-12 text-neutral-50 mb-2'>
          <svg
            width='12'
            height='12'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            className={tc.status === 'completed' ? 'text-green-500' : 'text-amber-500'}
          >
            {tc.status === 'completed' ? (
              <polyline points='20 6 9 17 4 12' />
            ) : (
              <>
                <circle cx='12' cy='12' r='10' />
                <polyline points='12 6 12 12 16 14' />
              </>
            )}
          </svg>
          <span>{tc.display_name}</span>
          {tc.description && <span className='text-neutral-40'>&mdash; {tc.description}</span>}
        </div>
      ))}
      {hiddenCount > 0 && !expanded && (
        <button type='button' onClick={() => setExpanded(true)} className='text-12 text-primary hover:text-primary/80 mt-2 flex items-center gap-4'>
          <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <polyline points='6 9 12 15 18 9' />
          </svg>
          {hiddenCount} more tool{hiddenCount > 1 ? 's' : ''}
        </button>
      )}
      {expanded && hiddenCount > 0 && (
        <button type='button' onClick={() => setExpanded(false)} className='text-12 text-primary hover:text-primary/80 mt-2 flex items-center gap-4'>
          <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <polyline points='18 15 12 9 6 15' />
          </svg>
          Show less
        </button>
      )}
    </div>
  );
};

/**
 * Group consecutive tool_call blocks together so they render as a single collapsible unit.
 * Other block types pass through as-is.
 */
type GroupedBlock = { kind: 'single'; block: ContentBlock; index: number } | { kind: 'tool_group'; calls: { key: string; toolCall: ActiveToolCall }[] };

function groupContentBlocks(blocks: ContentBlock[], messageId: string): GroupedBlock[] {
  const groups: GroupedBlock[] = [];
  let currentToolGroup: { key: string; toolCall: ActiveToolCall }[] | null = null;

  blocks.forEach((block, index) => {
    if (block.type === 'tool_call') {
      if (!currentToolGroup) {
        currentToolGroup = [];
      }
      currentToolGroup.push({ key: `${messageId}-block-${index}`, toolCall: block.toolCall });
    } else {
      if (currentToolGroup) {
        groups.push({ kind: 'tool_group', calls: currentToolGroup });
        currentToolGroup = null;
      }
      groups.push({ kind: 'single', block, index });
    }
  });

  if (currentToolGroup) {
    groups.push({ kind: 'tool_group', calls: currentToolGroup });
  }

  return groups;
}

/** Renders a single grouped block (tool group or content block). */
const RenderGroupedBlock: React.FC<{ group: GroupedBlock; messageId: string }> = ({ group, messageId }) => {
  if (group.kind === 'tool_group') {
    return <ToolCallGroup calls={group.calls} />;
  }

  const { block, index } = group;
  const key = `${messageId}-block-${index}`;

  if (block.type === 'text') {
    return (
      <div key={key} className='text-14 leading-relaxed mb-8 whitespace-pre-wrap bg-white text-neutral-90'>
        <InlineMarkdown text={block.text} />
      </div>
    );
  }

  if (block.type === 'rich') {
    const content = block.content;
    if (content.type === 'stats' && content.stats) {
      return (
        <div key={key} className='flex flex-wrap gap-16 w-full mb-16'>
          {content.stats.map((stat) => (
            <StatCard key={`${key}-stat-${stat.label}`} {...stat} />
          ))}
        </div>
      );
    }
    if (content.type === 'table' && content.table) {
      return <RichTable key={key} data={content.table} />;
    }
    if (content.type === 'list' && content.list) {
      return (
        <div key={key} className='mb-16 bg-white rounded-12 border border-neutral-20 p-16 shadow-sm'>
          <h4 className='text-14 font-semibold text-neutral-80 mb-8'>{content.list.title}</h4>
          {content.list.ordered ? (
            <ol className='list-decimal list-inside space-y-4'>
              {content.list.items.map((item, i) => (
                <li key={`${key}-item-${i}`} className='text-14 text-neutral-70 leading-relaxed'>
                  <InlineMarkdown text={item} />
                </li>
              ))}
            </ol>
          ) : (
            <ul className='list-disc list-inside space-y-4'>
              {content.list.items.map((item, i) => (
                <li key={`${key}-item-${i}`} className='text-14 text-neutral-70 leading-relaxed'>
                  <InlineMarkdown text={item} />
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    if (content.type === 'action-card' && content.action) {
      return (
        <div key={key} className='mb-16'>
          <button
            type='button'
            className='inline-flex items-center gap-8 px-16 py-8 bg-white border border-primary text-primary rounded-8 text-14 font-medium hover:bg-green-10 transition-colors shadow-sm'
          >
            <IconExternalLink size={16} />
            {content.action.label}
          </button>
        </div>
      );
    }
    return null;
  }

  return null;
};

/**
 * Split content blocks into thinking (tool calls + intermediate text) and answer phases.
 * Everything up to and including the last tool_call block is "thinking".
 * Everything after is "answer".
 */
function splitThinkingAndAnswer(blocks: ContentBlock[]): {
  thinkingBlocks: ContentBlock[];
  answerBlocks: ContentBlock[];
} {
  let lastToolCallIdx = -1;
  for (let i = blocks.length - 1; i >= 0; i--) {
    if (blocks[i].type === 'tool_call') {
      lastToolCallIdx = i;
      break;
    }
  }

  if (lastToolCallIdx === -1) {
    return { thinkingBlocks: [], answerBlocks: blocks };
  }

  return {
    thinkingBlocks: blocks.slice(0, lastToolCallIdx + 1),
    answerBlocks: blocks.slice(lastToolCallIdx + 1),
  };
}

/** Extract ActiveToolCall objects from content blocks. */
function extractToolCalls(blocks: ContentBlock[]): ActiveToolCall[] {
  return blocks.reduce<ActiveToolCall[]>((acc, block) => {
    if (block.type === 'tool_call') {
      return [...acc, block.toolCall];
    }
    return acc;
  }, []);
}

export const MessageItem: React.FC<MessageItemProps> = ({ message }) => {
  const { sender, isThinking, text, id } = message;
  const isUser = sender === 'user';

  const grouped = useMemo(() => groupContentBlocks(message.contentBlocks, id), [message.contentBlocks, id]);

  const { thinkingBlocks, answerBlocks } = useMemo(() => splitThinkingAndAnswer(message.contentBlocks), [message.contentBlocks]);

  const thinkingToolCalls = useMemo(() => extractToolCalls(thinkingBlocks), [thinkingBlocks]);

  const answerGrouped = useMemo(() => groupContentBlocks(answerBlocks, id), [answerBlocks, id]);

  const hasThinkingSection = thinkingBlocks.length > 0 || (message.thinkingContent && message.thinkingContent.length > 0);

  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} mb-24 animate-fade-in`}>
      <div className={`flex max-w-[90%] ${isUser ? 'flex-row-reverse' : 'flex-row'} gap-16`}>
        {/* Avatar */}
        <div className='flex-shrink-0'>
          {isUser ? (
            <div className='w-32 h-32 rounded-full bg-neutral-20 flex items-center justify-center overflow-hidden'>
              <IconUserAvatar size={20} className='text-neutral-50' />
            </div>
          ) : (
            <div className={`w-32 h-32 rounded-full flex items-center justify-center ${isThinking ? 'bg-white shadow-md' : 'bg-transparent'}`}>
              <div className={`${isThinking ? 'animate-pulse' : ''} text-primary`}>
                {isThinking ? (
                  <div className='relative'>
                    <div className='absolute inset-0 bg-primary/30 rounded-full blur-md animate-pulse' />
                    <IconFlashFilled size={24} />
                  </div>
                ) : (
                  <IconFlashFilled size={24} />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} flex-1 min-w-0`}>
          {isUser ? (
            text && (
              <div className='rounded-16 text-14 leading-relaxed shadow-sm mb-8 whitespace-pre-wrap px-20 py-12 bg-neutral-10 text-neutral-90 border border-neutral-20 rounded-tr-none'>
                {text}
              </div>
            )
          ) : (
            <>
              {isThinking ? (
                /* Streaming in progress: show all blocks inline as they arrive (no split) */
                <>
                  {grouped.length > 0 ? (
                    grouped.map((group, gIndex) => <RenderGroupedBlock key={`${id}-tg-${gIndex}`} group={group} messageId={id} />)
                  ) : (
                    <div className='flex items-center gap-4 text-neutral-50 text-14 mt-4'>
                      <span>Thinking...</span>
                    </div>
                  )}

                  {/* Streaming indicator */}
                  {message.contentBlocks.length > 0 && (
                    <div className='flex items-center gap-4 text-neutral-40 text-12 mt-4'>
                      <div className='w-4 h-4 rounded-full bg-primary animate-pulse' />
                      <span>Processing...</span>
                    </div>
                  )}
                </>
              ) : (
                /* Streaming complete: two-phase display */
                <>
                  {/* Thinking section (collapsible) */}
                  {hasThinkingSection && (
                    <ActivityFeed toolCalls={thinkingToolCalls} thinkingContent={message.thinkingContent} isCollapsible defaultCollapsed />
                  )}

                  {/* Answer section */}
                  {answerGrouped.map((group, gIndex) => (
                    <RenderGroupedBlock key={`${id}-ans-${gIndex}`} group={group} messageId={id} />
                  ))}
                </>
              )}
            </>
          )}

          {/* Action Buttons (Copy, Like, Dislike) for Bot */}
          {!isThinking && !isUser && (
            <div className='w-full flex items-center gap-12 mt-12 pt-12 border-t border-neutral-20 ml-4'>
              <button className='text-neutral-40 hover:text-neutral-60 transition-colors p-4 rounded hover:bg-neutral-10' aria-label='Like'>
                <IconThumbsUp size={16} />
              </button>
              <button className='text-neutral-40 hover:text-neutral-60 transition-colors p-4 rounded hover:bg-neutral-10' aria-label='Dislike'>
                <IconThumbsDown size={16} />
              </button>
              <button className='text-neutral-40 hover:text-neutral-60 transition-colors p-4 rounded hover:bg-neutral-10' aria-label='Copy'>
                <IconCopy size={16} />
              </button>
            </div>
          )}

          {/* Action Buttons (Edit, Delete) for User */}
          {!isThinking && isUser && (
            <div className='flex items-center gap-8 mt-4 mr-4 justify-end'>
              <button className='w-16 h-16 flex items-center justify-center text-neutral-40 hover:text-neutral-60 transition-colors' aria-label='Edit'>
                <img src={IconEdit} alt='Edit' className='w-full h-full' />
              </button>
              <button className='w-16 h-16 flex items-center justify-center text-neutral-40 hover:text-neutral-60 transition-colors' aria-label='Delete'>
                <img src={IconTrash} alt='Delete' className='w-full h-full' />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
